<template>
  <div class="card mb-4 radius-7 plan-locked-wrapper initial-message-config" :class="{ 'plan-locked-wrapper--message': disabledByPlan }">
    <PlanFeatureLocked
      v-if="disabledByPlan"
      overlay
      title="Mensagem inicial bloqueada pelo plano"
      message="O plano atual não inclui atendimento automático pelo WhatsApp."
    />

    <div class="card-head box-shadow bg-white d-flex align-items-start justify-content-between gap-3 p-15 p-sm-20 p-md-25 radius-7">
      <div>
        <h5 class="card-title mb-1">
          <i class="ri-message-3-line me-2"></i>Mensagem inicial da URA
        </h5>
        <p class="text-muted mb-0">Personalize a apresentação, o aviso e os emojis exibidos no primeiro menu.</p>
      </div>
      <button
        v-if="!isEditing && configuration"
        type="button"
        class="btn btn-outline-primary btn-sm flex-shrink-0"
        :disabled="disabledByPlan"
        @click="startEditing"
      >
        <i class="ri-edit-line me-1"></i>Editar
      </button>
    </div>

    <div class="card-body position-relative" :class="{ 'opacity-50': disabledByPlan }">
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Carregando...</span></div>
      </div>

      <div v-else-if="loadError && !configuration" class="alert alert-danger d-flex justify-content-between align-items-center gap-3">
        <span><i class="ri-alert-line me-2"></i>{{ loadError }}</span>
        <button type="button" class="btn btn-sm btn-outline-danger" @click="loadConfiguration">Tentar novamente</button>
      </div>

      <template v-else-if="configuration">
        <div v-if="isEditing" class="row g-4">
          <div class="col-xl-7">
            <div class="mb-4">
              <div class="d-flex justify-content-between align-items-center gap-2 mb-2">
                <label for="initial-presentation" class="form-label mb-0">Texto de apresentação</label>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="insertClientName">
                  Inserir {client_name}
                </button>
              </div>
              <textarea
                id="initial-presentation"
                ref="presentationTextarea"
                v-model="draft.presentation_text"
                class="form-control message-textarea"
                rows="6"
                maxlength="2000"
                :disabled="saving"
              ></textarea>
              <small class="text-muted d-block mt-1">
                Use <code>{client_name}</code> onde quiser inserir o primeiro nome. O marcador é opcional e a formatação do WhatsApp é aceita.
              </small>
            </div>

            <div class="mb-4">
              <label for="initial-notice" class="form-label">Aviso depois das opções <span class="text-muted fw-normal">(opcional)</span></label>
              <textarea
                id="initial-notice"
                v-model="draft.notice_text"
                class="form-control message-textarea"
                rows="4"
                maxlength="1500"
                :disabled="saving"
                placeholder="Ex.: ⚠️ IMPORTANTE:&#10;Os valores serão reajustados a partir de..."
              ></textarea>
            </div>

            <div>
              <label class="form-label">Emojis das opções</label>
              <p class="text-muted small mb-3">Informe no máximo um emoji. Os nomes e o funcionamento das opções não serão alterados.</p>
              <div class="option-list">
                <div v-for="option in draft.options" :key="option.id" class="option-row">
                  <span class="option-number">{{ optionNumber(option.id) }}</span>
                  <input
                    v-model="option.emoji"
                    class="form-control emoji-input"
                    maxlength="16"
                    :aria-label="`Emoji de ${option.name}`"
                    :disabled="saving"
                    placeholder="—"
                  />
                  <span class="option-name">{{ option.name }}</span>
                </div>
                <div class="option-row">
                  <span class="option-number">0</span>
                  <input
                    v-model="draft.attendant_emoji"
                    class="form-control emoji-input"
                    maxlength="16"
                    aria-label="Emoji de Falar com atendente"
                    :disabled="saving"
                    placeholder="—"
                  />
                  <span class="option-name">Falar com atendente</span>
                </div>
              </div>
            </div>
          </div>

          <div class="col-xl-5">
            <div class="preview-panel">
              <div class="preview-panel__title"><i class="ri-whatsapp-line me-2"></i>Pré-visualização</div>
              <pre class="preview-message">{{ previewMessage }}</pre>
            </div>
          </div>

          <div class="col-12">
            <div v-if="validationError" class="alert alert-warning mb-3">{{ validationError }}</div>
            <div v-if="loadError" class="alert alert-danger mb-3">{{ loadError }}</div>
            <div class="d-flex flex-wrap gap-2">
              <button type="button" class="btn btn-secondary" :disabled="saving" @click="cancelEditing">Cancelar</button>
              <button type="button" class="btn btn-success" :disabled="saving || Boolean(validationError)" @click="saveConfiguration">
                <span v-if="saving" class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                <i v-else class="ri-save-line me-2"></i>{{ saving ? 'Salvando...' : 'Salvar configuração' }}
              </button>
            </div>
          </div>
        </div>

        <div v-else class="row g-4">
          <div class="col-xl-7">
            <div class="summary-block">
              <span class="summary-label">Apresentação</span>
              <pre>{{ configuration.presentation_text }}</pre>
            </div>
            <div class="summary-block mb-0">
              <span class="summary-label">Aviso</span>
              <pre>{{ configuration.notice_text || 'Nenhum aviso configurado.' }}</pre>
            </div>
          </div>
          <div class="col-xl-5">
            <div class="preview-panel">
              <div class="preview-panel__title"><i class="ri-whatsapp-line me-2"></i>Pré-visualização atual</div>
              <pre class="preview-message">{{ currentPreviewMessage }}</pre>
            </div>
          </div>
        </div>

        <div v-if="successMessage" class="alert alert-success mt-4 mb-0">{{ successMessage }}</div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import PlanFeatureLocked from "../Common/PlanFeatureLocked.vue";

type MenuOption = {
  id: number;
  name: string;
  type: string;
  emoji: string | null;
};

type InitialMessageConfiguration = {
  presentation_text: string;
  notice_text: string | null;
  attendant_emoji: string | null;
  options: MenuOption[];
};

const cloneConfiguration = (configuration: InitialMessageConfiguration): InitialMessageConfiguration => ({
  presentation_text: configuration.presentation_text || "",
  notice_text: configuration.notice_text || "",
  attendant_emoji: configuration.attendant_emoji || "",
  options: (configuration.options || []).map(option => ({ ...option, emoji: option.emoji || "" })),
});

const smallCaps = (text: string) => {
  const characters: Record<string, string> = {
    a: "ᴀ", b: "ʙ", c: "ᴄ", d: "ᴅ", e: "ᴇ", f: "ғ", g: "ɢ", h: "ʜ",
    i: "ɪ", j: "ᴊ", k: "ᴋ", l: "ʟ", m: "ᴍ", n: "ɴ", o: "ᴏ", p: "ᴘ",
    q: "ǫ", r: "ʀ", s: "s", t: "ᴛ", u: "ᴜ", v: "ᴠ", w: "ᴡ", x: "x", y: "ʏ", z: "ᴢ",
  };
  return text.toLowerCase().split("").map(character => characters[character] || character).join("");
};

const buildPreview = (configuration: InitialMessageConfiguration) => {
  const presentation = (configuration.presentation_text || "").replaceAll("{client_name}", "Andressa").trim();
  const menu = (configuration.options || []).map((option, index) => {
    const emoji = (option.emoji || "").trim();
    return `*${index + 1}* - ${emoji ? `${emoji} ` : ""}${option.name}`;
  });
  const attendantEmoji = (configuration.attendant_emoji || "").trim();
  menu.push(`*0* - ${attendantEmoji ? `${attendantEmoji} ` : ""}Falar com atendente`);

  return [
    presentation,
    menu.join("\n"),
    (configuration.notice_text || "").trim(),
    `*${smallCaps("Digite 'sair' para finalizar")}*`,
  ].filter(Boolean).join("\n\n");
};

export default defineComponent({
  name: "InitialWhatsappMessageConfig",
  components: { PlanFeatureLocked },
  props: {
    disabledByPlan: { type: Boolean, default: false },
  },
  setup(props) {
    const store = useStore();
    const isEditing = ref(false);
    const successMessage = ref("");
    const presentationTextarea = ref<HTMLTextAreaElement | null>(null);
    const draft = ref<InitialMessageConfiguration>({
      presentation_text: "",
      notice_text: "",
      attendant_emoji: "",
      options: [],
    });

    const configuration = computed<InitialMessageConfiguration | null>(() => store.getters.getWhatsappInitialMessage);
    const loading = computed(() => store.getters.getLoadingWhatsappInitialMessage);
    const saving = computed(() => store.getters.getSavingWhatsappInitialMessage);
    const loadError = computed(() => store.getters.getWhatsappInitialMessageError);
    const validationError = computed(() => {
      if (!draft.value.presentation_text.trim()) return "Informe o texto de apresentação.";
      if (draft.value.presentation_text.length > 2000) return "A apresentação deve ter no máximo 2.000 caracteres.";
      if ((draft.value.notice_text || "").length > 1500) return "O aviso deve ter no máximo 1.500 caracteres.";
      return "";
    });
    const previewMessage = computed(() => buildPreview(draft.value));
    const currentPreviewMessage = computed(() => configuration.value ? buildPreview(configuration.value) : "");

    const loadConfiguration = async () => {
      if (!props.disabledByPlan) await store.dispatch("fetchWhatsappInitialMessage").catch(() => null);
    };
    const startEditing = () => {
      if (!configuration.value) return;
      draft.value = cloneConfiguration(configuration.value);
      successMessage.value = "";
      isEditing.value = true;
    };
    const cancelEditing = () => {
      isEditing.value = false;
      successMessage.value = "";
    };
    const saveConfiguration = async () => {
      if (validationError.value) return;
      try {
        await store.dispatch("saveWhatsappInitialMessage", draft.value);
        isEditing.value = false;
        successMessage.value = "Configuração salva. Ela será usada na próxima nova sessão da URA.";
      } catch {
        // O erro já é exposto pelo módulo de configuração.
      }
    };
    const insertClientName = async () => {
      const textarea = presentationTextarea.value;
      const marker = "{client_name}";
      if (!textarea) {
        draft.value.presentation_text += marker;
        return;
      }
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      draft.value.presentation_text = `${draft.value.presentation_text.slice(0, start)}${marker}${draft.value.presentation_text.slice(end)}`;
      await nextTick();
      textarea.focus();
      textarea.setSelectionRange(start + marker.length, start + marker.length);
    };
    const optionNumber = (optionId: number) => {
      const index = draft.value.options.findIndex(option => option.id === optionId);
      return index + 1;
    };

    onMounted(loadConfiguration);
    watch(() => props.disabledByPlan, disabled => {
      if (!disabled && !configuration.value) loadConfiguration();
    });

    return {
      configuration,
      loading,
      saving,
      loadError,
      isEditing,
      successMessage,
      presentationTextarea,
      draft,
      validationError,
      previewMessage,
      currentPreviewMessage,
      loadConfiguration,
      startEditing,
      cancelEditing,
      saveConfiguration,
      insertClientName,
      optionNumber,
    };
  },
});
</script>

<style scoped>
.plan-locked-wrapper {
  overflow: hidden;
  position: relative;
}

.plan-locked-wrapper--message {
  min-height: 320px;
}

.message-textarea {
  line-height: 1.55;
  resize: vertical;
}

.option-list {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.option-row {
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: 32px 62px minmax(0, 1fr);
  min-height: 58px;
  padding: 9px 14px;
}

.option-row + .option-row {
  border-top: 1px solid #e5e7eb;
}

.option-number {
  font-weight: 700;
  text-align: center;
}

.emoji-input {
  font-size: 22px;
  padding-left: 8px;
  padding-right: 8px;
  text-align: center;
}

.option-name {
  font-weight: 600;
  min-width: 0;
}

.preview-panel {
  background: #efeae2;
  border-radius: 10px;
  min-height: 100%;
  padding: 16px;
}

.preview-panel__title {
  color: #4b5563;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 12px;
  text-transform: uppercase;
}

.preview-message {
  background: #202c33;
  border-radius: 8px;
  color: #f8fafc;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
  min-height: 180px;
  padding: 16px;
  white-space: pre-wrap;
  word-break: break-word;
}

.summary-block {
  margin-bottom: 24px;
}

.summary-label {
  color: #6b7280;
  display: block;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 7px;
  text-transform: uppercase;
}

.summary-block pre {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  font-family: inherit;
  margin: 0;
  padding: 14px;
  white-space: pre-wrap;
}

@media (max-width: 575px) {
  .card-head {
    flex-direction: column;
  }

  .option-row {
    gap: 8px;
    grid-template-columns: 28px 56px minmax(0, 1fr);
    padding-left: 9px;
    padding-right: 9px;
  }
}
</style>

<style>
body.dark .initial-message-config .preview-panel {
  background: #111b21;
}

body.dark .initial-message-config .summary-block pre,
body.dark .initial-message-config .option-list {
  background: #172033;
  border-color: #334155;
  color: #e5e7eb;
}

body.dark .initial-message-config .option-row + .option-row {
  border-color: #334155;
}
</style>
